// Pure helpers for the Dashboard tile's stale-queued chip.
//
// The chip surfaces — at a glance, before the operator clicks into
// Changes → Approved & Queued — how many of a product's queued entries
// have gone stale since they were approved. Staleness here matches
// INTERFACE-SPECIFICATION.md §8.1: a queued entry is stale when one or
// more intermediate manifest entries went in_effect between its
// approved_at and its effective_from.utc, so the queued snapshot will
// silently overwrite those intermediate edits when its time-gate
// fires. The detectStaleQueuedEntry server function (PR 4.2 item 10b)
// returns that intermediate list per queued entry; this helper
// aggregates the per-entry signal up to a per-product count for the
// Dashboard surface.
//
// Kept pure (no Svelte runes, no fetch) so the Node-side unit suite
// can exercise it directly. ProductCard.svelte threads the API state
// in and renders the count.

import type { ManifestVersionEntry, StaleQueuedIntermediate } from './types';

// stalenessFor: lookup table keyed by catalogue_version. The SPA
// populates it lazily as each queued entry's staleness call lands;
// `undefined` means "not yet fetched", `[]` means "fetched and fresh",
// a non-empty array means "stale by this many intermediate entries".
export type StalenessLookup = Record<string, StaleQueuedIntermediate[] | undefined>;

// productStaleQueuedCount returns the number of queued entries for the
// supplied product that have a non-empty intermediate list. An entry
// whose staleness has not yet been fetched (undefined in the lookup)
// counts as zero — pending fetches do not render the chip; only
// confirmed-stale entries do. Same conservative discipline the
// per-card banner in ApprovedQueuedList.svelte uses.
export function productStaleQueuedCount(
  queuedVersions: ManifestVersionEntry[],
  stalenessFor: StalenessLookup
): number {
  let count = 0;
  for (const version of queuedVersions) {
    const intermediate = stalenessFor[version.catalogue_version];
    if (Array.isArray(intermediate) && intermediate.length > 0) count += 1;
  }
  return count;
}

// productStaleQueuedLabel formats the chip text. Single-stale and
// multi-stale variants match the existing "N change[s] queued" copy on
// the same tile so the two chips read in parallel.
export function productStaleQueuedLabel(count: number): string {
  if (count === 0) return '';
  if (count === 1) return '1 stale';
  return `${count} stale`;
}
